import * as React from 'react'
import { Combobox, Option } from 'uiComponents/combobox/combobox'
import { SegmentedControl, SegmentOption } from 'uiComponents/segmentedControl/segmentedControl'
import { useGetAllCategoriesForSelect, useGetAllProductsForSelect } from 'products/redux'
import { TIME_SLOTS } from 'capacity/copy'
import { SearchKind, SearchSelection, STATUS_VALUES, StatusFilter } from './domain/listFilters'
import './listToolbar.scss'

export type TimeSlotsView = 'categories' | 'products'

const optionValue = (kind: SearchKind, id: string) => `${kind}:${id}`

const parseValue = (value: string): SearchSelection => {
    const [kind, ...rest] = value.split(':')
    return { kind: kind as SearchKind, id: rest.join(':') }
}

const withKind = (option: Option, kind: SearchKind): Option => ({
    ...option,
    value: optionValue(kind, option.value),
    renderLabel: (
        <>
            {option.label}
            <span className="timeslots-search-kind">{TIME_SLOTS.kind[kind]}</span>
        </>
    ),
})

export function useSearchOptions(): Option[] {
    const categories = useGetAllCategoriesForSelect()
    const products = useGetAllProductsForSelect()
    return React.useMemo(
        () => [
            ...categories.map(({ label, value }) => withKind({ label, value }, 'category')),
            ...products.map((option) => withKind(option, 'product')),
        ],
        [categories, products],
    )
}

export const selectionLabel = (selection: SearchSelection | null, options: Option[]): string | null =>
    selection
        ? options.find((option) => option.value === optionValue(selection.kind, selection.id))?.label ?? null
        : null

interface TimeSlotsToolbarProps {
    options: Option[]
    optionsLoading: boolean
    selection: SearchSelection | null
    onSelect: (selection: SearchSelection | null) => void
    view: TimeSlotsView
    onViewChange: (view: TimeSlotsView) => void
    showViewSwitch: boolean
    viewBusy: boolean
    status: StatusFilter
    onStatusChange: (status: StatusFilter) => void
    counts: Record<StatusFilter, number>
}

export function TimeSlotsToolbar({
    options,
    optionsLoading,
    selection,
    onSelect,
    view,
    onViewChange,
    showViewSwitch,
    viewBusy,
    status,
    onStatusChange,
    counts,
}: TimeSlotsToolbarProps) {
    const statusOptions = React.useMemo<SegmentOption<StatusFilter>[]>(
        () => [
            { value: 'all', label: `${TIME_SLOTS.all} ${counts.all}` },
            ...STATUS_VALUES.map((value) => ({
                value: value as StatusFilter,
                label: `${TIME_SLOTS.status[value]} ${counts[value]}`,
            })),
        ],
        [counts],
    )
    const viewOptions: SegmentOption<TimeSlotsView>[] = [
        {
            value: 'categories',
            label: TIME_SLOTS.views.categories,
            disabled: viewBusy,
            disabledReason: TIME_SLOTS.viewBusy,
        },
        {
            value: 'products',
            label: TIME_SLOTS.views.products,
            disabled: viewBusy,
            disabledReason: TIME_SLOTS.viewBusy,
        },
    ]

    return (
        <div className="timeslots-toolbar">
            <div className="timeslots-search">
                <Combobox
                    options={options}
                    loading={optionsLoading}
                    value={selection ? optionValue(selection.kind, selection.id) : null}
                    searchPlaceholder={TIME_SLOTS.search}
                    onChange={(next) => onSelect(typeof next === 'string' ? parseValue(next) : null)}
                />
            </div>
            <div className="timeslots-toolbar-controls">
                {showViewSwitch && (
                    <SegmentedControl
                        size="compact"
                        fit
                        ariaLabel={TIME_SLOTS.viewLabel}
                        options={viewOptions}
                        value={view}
                        onChange={onViewChange}
                    />
                )}
                <SegmentedControl
                    size="compact"
                    fit
                    ariaLabel={TIME_SLOTS.statusLabel}
                    options={statusOptions}
                    value={status}
                    onChange={onStatusChange}
                />
            </div>
        </div>
    )
}
